// Comfort & accessibility settings (owner-approved 2026-11-02), persisted in the versioned profile (save.ts v4, `a11y`).
// Pure: no DOM. main.ts applies them (src/render/a11y.ts FLASH / CB, view.shakeScale, --ui-scale, --hud-scale).
import { CB_MODES, CbMode, FLASH_MODES, FlashMode } from '../render/a11y';

export type TextSize = 'small' | 'auto' | 'large' | 'tv';
export interface A11yPrefs {
  /** Reduce flashing: off (as authored) / reduced / minimal */
  flashes: FlashMode;
  /** camera shake, percent of the authored amount (0..100) */
  shake: number;
  /** brief freeze-frames on heavy hits */
  hitstop: boolean;
  /** colour-blind safe palette + shape cues */
  colorblind: CbMode;
  /** text size S / M / L / XL (stored with the long-standing 'uisize' values so ?ui= and the QA scripts keep working) */
  textSize: TextSize;
  /** in-run HUD scale, percent (70..150) */
  hudScale: number;
}

export const TEXT_SIZES: readonly TextSize[] = ['small', 'auto', 'large', 'tv'];
/** multiplier on the fluid root unit --u (src/style.css); M is the automatic fit */
export const TEXT_SCALE: Record<TextSize, number> = { small: 0.88, auto: 1, large: 1.2, tv: 1.4 };
export const SHAKE_STEPS = [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100] as const;
export const HUD_STEPS = [70, 80, 90, 100, 110, 125, 150] as const;

export function defaultA11y(): A11yPrefs {
  return { flashes: 'off', shake: 100, hitstop: true, colorblind: 'off', textSize: 'auto', hudScale: 100 };
}

/** the pre-v4 localStorage keys (arena.flashes 'normal'|'reduced', arena.shake 'normal'|'reduced', arena.uisize) */
export interface LegacyA11y { flashes?: string | null; shake?: string | null; uisize?: string | null }

const nearest = (v: number, steps: readonly number[]) => steps.reduce((a, b) => (Math.abs(b - v) < Math.abs(a - v) ? b : a));
/** 'normal' (older Text size option) and anything unknown → M */
export const textSizeOf = (v: unknown): TextSize => (TEXT_SIZES.includes(v as TextSize) ? (v as TextSize) : 'auto');
/** flashes value (incl. the pre-v4 'normal') → mode */
export const flashOf = (v: unknown): FlashMode => (FLASH_MODES.includes(v as FlashMode) ? (v as FlashMode) : 'off');

/** Upgrade any stored / imported shape. When the profile has no a11y block yet (v1/v2), seed it from the legacy keys. */
export function migrateA11y(raw: unknown, legacy?: LegacyA11y): A11yPrefs {
  const p = defaultA11y();
  if (!raw || typeof raw !== 'object') {
    if (legacy) {
      if (legacy.flashes === 'reduced') p.flashes = 'reduced';
      if (legacy.shake === 'reduced') p.shake = 0; // the old 'Reduced' turned camera shake off
      if (legacy.uisize) p.textSize = textSizeOf(legacy.uisize);
    }
    return p;
  }
  const r = raw as Partial<Record<keyof A11yPrefs, unknown>>;
  p.flashes = flashOf(r.flashes);
  if (typeof r.shake === 'number' && Number.isFinite(r.shake)) p.shake = nearest(Math.max(0, Math.min(100, r.shake)), SHAKE_STEPS);
  if (typeof r.hitstop === 'boolean') p.hitstop = r.hitstop;
  p.colorblind = CB_MODES.includes(r.colorblind as CbMode) ? (r.colorblind as CbMode) : 'off';
  p.textSize = textSizeOf(r.textSize);
  if (typeof r.hudScale === 'number' && Number.isFinite(r.hudScale)) p.hudScale = nearest(r.hudScale, HUD_STEPS);
  return p;
}
